import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import {
  UserAccessibility,
  AccessPermissions,
} from "../store/slices/misc/accessibility";

/**
 * Hook to get current user's accessibility data from Redux store
 */
export const useAccessibility = (): UserAccessibility => {
  return useSelector((state: RootState) => state.accessibility.data);
};

/**
 * Check if user has access to a specific module
 */
export const hasModuleAccess = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  if (!accessibility || !moduleName) return false;
  return accessibility[moduleName]?.access === true;
};

/**
 * Check if user has specific permission for a module
 */
export const hasPermission = (
  accessibility: UserAccessibility,
  moduleName: string,
  permission: keyof AccessPermissions
): boolean => {
  if (!accessibility || !moduleName) return false;
  const module = accessibility[moduleName];
  if (!module) return false;

  // For Activities, Tasks, and Baseline modules, check individual permissions directly
  if (
    moduleName === "Activities" ||
    moduleName === "Tasks" ||
    moduleName === "Baseline"
  ) {
    return module[permission] === 1;
  }

  // For other modules, check module access first
  if (!module.access) return false;
  return module[permission] === 1;
};

/**
 * Check if user can add items in a module
 */
export const canAdd = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  return hasPermission(accessibility, moduleName, "add");
};

/**
 * Check if user can view items in a module
 */
export const canView = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  return hasPermission(accessibility, moduleName, "view");
};

/**
 * Check if user can edit items in a module
 */
export const canEdit = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  return hasPermission(accessibility, moduleName, "edit");
};

/**
 * Check if user can approve items in a module
 */
export const canApprove = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  return hasPermission(accessibility, moduleName, "approve");
};

/**
 * Check if user can reject items in a module
 */
export const canReject = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  return hasPermission(accessibility, moduleName, "reject");
};

/**
 * Check if user can cancel items in a module
 */
export const canCancel = (
  accessibility: UserAccessibility,
  moduleName: string
): boolean => {
  return hasPermission(accessibility, moduleName, "cancel");
};

/**
 * Get all permissions for a specific module
 */
export const getModulePermissions = (
  accessibility: UserAccessibility,
  moduleName: string
): AccessPermissions | null => {
  if (!accessibility || !moduleName) return null;
  const module = accessibility[moduleName];
  if (!module) return null;

  // For Activities, Tasks, and Baseline modules, return permissions regardless of module access
  if (
    moduleName === "Activities" ||
    moduleName === "Tasks" ||
    moduleName === "Baseline"
  ) {
    return {
      add: module.add,
      view: module.view,
      edit: module.edit,
      approve: module.approve,
      reject: module.reject,
      cancel: module.cancel,
    };
  }

  // For other modules, check module access first
  if (!module.access) return null;

  return {
    add: module.add,
    view: module.view,
    edit: module.edit,
    approve: module.approve,
    reject: module.reject,
    cancel: module.cancel,
  };
};

/**
 * Check if user has access to a specific route
 */
export const hasRouteAccess = (
  accessibility: UserAccessibility,
  routePath: string
): boolean => {
  if (!accessibility || !routePath) return false;

  for (const moduleName in accessibility) {
    const module = accessibility[moduleName];
    if (module?.access && module.routes) {
      const route = module.routes.find((r) => r.path === routePath);
      if (route && route.permission) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Get the module name for a specific route
 */
export const getModuleForRoute = (
  accessibility: UserAccessibility,
  routePath: string
): string | null => {
  if (!accessibility || !routePath) return null;

  for (const moduleName in accessibility) {
    const module = accessibility[moduleName];
    if (module?.access && module.routes) {
      const route = module.routes.find((r) => r.path === routePath);
      if (route) {
        return moduleName;
      }
    }
  }
  return null;
};

// Re-export types for convenience
export type {
  UserAccessibility,
  AccessPermissions,
} from "../store/slices/misc/accessibility";
